import { __awaiter } from "tslib";
import { get } from 'svelte/store';
import { maskPiiFields } from '@twm-libs/utils';
import { AppState } from '../etc/stores';
import { Container } from './container';
import { SessionManager } from './session-manager';
import { getSiteSettings, isMobile } from '../etc/utils';
import sessionKeys from '../etc/sessionKeys';
import flagKeys from '../etc/flagKeys';
const defaultPropsSegmentio = {
    origin: 'widget',
    url: window.origin
};
let authenticatedOnWebsite = false;
AppState.subscribe(state => {
    authenticatedOnWebsite =
        !!state.website.authenticatedOnWebsite || !!state.website.userId;
});
class Analytics {
    constructor() {
        this.domain = 'https://www.googletagmanager.com';
        this.scriptId = 'twism-analytics-script';
        this.session = Container.get(SessionManager);
        this.trackingId = process.env.GA_ID;
        this.getFreeCoinsTitleFlow = () => {
            const isFreeCoinsFlow = get(AppState).website.authFlow === 'free-coins';
            return isFreeCoinsFlow ? ' Free Coins' : '';
        };
        this.getCategoryLoginState = () => {
            const { auth } = get(AppState);
            if (auth.connected)
                return 'logged-state';
            if (this.getIsGuest())
                return 'guest-state';
            return 'unknown-state';
        };
        this.getIsGuest = () => {
            if (typeof this.session.get(sessionKeys.GUEST) === 'boolean')
                return this.session.get(sessionKeys.GUEST);
            return !authenticatedOnWebsite;
        };
        this.getConnectAccountData = (categoryDefault) => {
            let category = categoryDefault || (this.getIsGuest() ? 'guest-state' : 'join-state');
            let prefixEvent = 'click';
            if (this.session.get(sessionKeys.COLLECT_COINS)) {
                category = `rewards-${this.getCategoryLoginState()}`;
                prefixEvent = 'click-collect-coins';
            }
            return { prefixEvent, category };
        };
        this.setSessionAnalyticsOnJoin = (next, guest) => {
            if (next === '/connect-account')
                this.session.set(sessionKeys.GUEST, guest);
        };
    }
    addGoogleAnalyticsScript() {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve, reject) => {
                const head = document.head || document.getElementsByTagName('head')[0];
                const link = document.createElement('link');
                link.href = this.domain;
                link.rel = 'preconnect';
                head.appendChild(link);
                const script = document.createElement('script');
                script.async = true;
                script.src = `${this.domain}/gtag/js?id=${this.trackingId}`;
                script.setAttribute('id', this.scriptId);
                head.appendChild(script);
                script.onload = resolve;
                script.onerror = reject;
            });
        });
    }
    init() {
        return __awaiter(this, void 0, void 0, function* () {
            if (window.document.getElementById(this.scriptId)) {
                return;
            }
            window.dataLayer = window.dataLayer || [];
            const gtagAddLayer = function () {
                window.dataLayer.push(arguments);
            };
            const PII_GTAG_FIELDS = ['email', 'mail', 'phone', 'name', 'birthday'];
            function stripGtagPii(data) {
                const safe = Object.assign({}, data);
                for (const field of PII_GTAG_FIELDS) {
                    delete safe[field];
                }
                return safe;
            }
            window.twismGtag = function (a1, a2, config) {
                // always specify where to send, so as not to send to another GA account
                if (config)
                    config = Object.assign(Object.assign({ event_label: window.origin }, stripGtagPii(config)), { send_to: process.env.GA_ID });
                gtagAddLayer(a1, a2, config);
            };
            gtagAddLayer('js', new Date());
            // removed to not receive shares from other GA accounts
            // gtagAddLayer("config", this.trackingId);
            try {
                yield this.addGoogleAnalyticsScript();
            }
            catch (err) {
                console.error('twism gtag failure');
                const s = window.document.getElementById(this.scriptId);
                if (s) {
                    s.remove();
                }
                return;
            }
            // ADD SEGMENT.IO
            try {
                this.addSegmentIOAnalyticsScript();
            }
            catch (error) {
                console.error('twism segment.io failure');
            }
        });
    }
    addSegmentIOAnalyticsScript() {
        //@ts-ignore
        !(function () {
            var analytics = (window.analytics = window.analytics || []);
            if (!analytics.initialize)
                if (analytics.invoked)
                    window.console &&
                        console.error &&
                        console.error('Segment snippet included twice.');
                else {
                    analytics.invoked = !0;
                    analytics.methods = [
                        'trackSubmit',
                        'trackClick',
                        'trackLink',
                        'trackForm',
                        'pageview',
                        'identify',
                        'reset',
                        'group',
                        'track',
                        'ready',
                        'alias',
                        'debug',
                        'page',
                        'once',
                        'off',
                        'on',
                        'addSourceMiddleware',
                        'addIntegrationMiddleware',
                        'setAnonymousId',
                        'addDestinationMiddleware'
                    ];
                    analytics.factory = function (e) {
                        return function () {
                            var t = Array.prototype.slice.call(arguments);
                            t.unshift(e);
                            analytics.push(t);
                            return analytics;
                        };
                    };
                    for (var e = 0; e < analytics.methods.length; e++) {
                        var key = analytics.methods[e];
                        analytics[key] = analytics.factory(key);
                    }
                    analytics.load = function (key, e) {
                        var t = document.createElement('script');
                        t.type = 'text/javascript';
                        t.async = !0;
                        t.src =
                            'https://cdn.segment.com/analytics.js/v1/' +
                                key +
                                '/analytics.min.js';
                        var n = document.getElementsByTagName('script')[0];
                        n.parentNode.insertBefore(t, n);
                        analytics._loadOptions = e;
                    };
                    analytics._writeKey = process.env.SEGMENT_ID;
                    analytics.SNIPPET_VERSION = '4.15.3';
                    analytics.load(process.env.SEGMENT_ID);
                    analytics.ready(() => (window.twismSegmentIO = window.analytics));
                }
        })();
    }
    sanitizeData(data) {
        if (!get(AppState).featureFlags.includes(flagKeys.SEGMENT_SANITIZER))
            return data;
        const customer = get(AppState).customer;
        const { properties } = maskPiiFields({
            properties: data,
            userId: customer ? customer.id : '',
            event: ''
        });
        return properties;
    }
    logEvent(name, category, platforms = ['segmentio', 'gtag'], additionalInfo = {}) {
        var _a;
        if (platforms.indexOf('segmentio') > -1) {
            const { provider } = getSiteSettings();
            (_a = window.twismSegmentIO) === null || _a === void 0 ? void 0 : _a.track(name, this.sanitizeData(Object.assign(Object.assign(Object.assign({ event_category: category }, additionalInfo), defaultPropsSegmentio), { provider, widget_mobile: isMobile() })));
        }
        if (platforms.indexOf('gtag') > -1) {
            window.twismGtag('event', name, {
                event_category: category
            });
        }
    }
    logPageView(name, path) {
        var _a;
        const { provider } = getSiteSettings();
        (_a = window.twismSegmentIO) === null || _a === void 0 ? void 0 : _a.track(`Viewed ${name} Page`, this.sanitizeData(Object.assign(Object.assign({ page_title: name, page_path: path }, defaultPropsSegmentio), { provider, widget_mobile: isMobile() })));
        window.twismGtag('event', 'page_view', {
            page_title: name,
            page_location: path,
            page_path: path
        });
    }
    identify(userId, traits) {
        var _a;
        (_a = window.twismSegmentIO) === null || _a === void 0 ? void 0 : _a.identify(userId, Object.assign(Object.assign({}, traits), { 'is-widget-user': true }));
    }
}
export default new Analytics();
